
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Activity, 
  Cpu, 
  Database, 
  Network, 
  Users, 
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface MetricData {
  timestamp: string;
  value: number;
}

interface SystemMetric {
  id: string;
  name: string;
  value: number;
  unit: string;
  status: 'healthy' | 'warning' | 'critical';
  threshold: number;
  history: MetricData[];
  icon: any;
}

const RealTimeMetrics: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetric[]>([
    {
      id: 'cpu',
      name: 'CPU Usage',
      value: 45,
      unit: '%',
      status: 'healthy',
      threshold: 80,
      history: [],
      icon: Cpu
    },
    {
      id: 'memory',
      name: 'Memory Usage',
      value: 62,
      unit: '%',
      status: 'healthy',
      threshold: 85,
      history: [],
      icon: Database
    },
    {
      id: 'activeUsers',
      name: 'Active Users',
      value: 127,
      unit: '',
      status: 'healthy',
      threshold: 1000,
      history: [],
      icon: Users
    },
    {
      id: 'responseTime',
      name: 'Response Time',
      value: 145,
      unit: 'ms',
      status: 'healthy',
      threshold: 500,
      history: [],
      icon: Clock
    },
    {
      id: 'networkLatency',
      name: 'Network Latency',
      value: 23,
      unit: 'ms',
      status: 'healthy',
      threshold: 100,
      history: [],
      icon: Network
    }
  ]);

  const { data: systemHealth } = useQuery({
    queryKey: ['system-health-realtime'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_system_health_metrics');
      if (error) throw error;
      return data || [];
    },
    refetchInterval: 5000 // Refresh every 5 seconds
  });

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prev => prev.map(metric => {
        const variance = 0.1;
        const newValue = Math.max(0, 
          metric.value + (Math.random() - 0.5) * metric.value * variance
        );
        
        const status = newValue > metric.threshold ? 'critical' :
                      newValue > metric.threshold * 0.8 ? 'warning' : 'healthy';
        
        const newDataPoint = {
          timestamp: new Date().toISOString(),
          value: Math.round(newValue)
        };

        return {
          ...metric,
          value: Math.round(newValue),
          status,
          history: [...metric.history.slice(-19), newDataPoint]
        };
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-white text-green-800';
      case 'warning': return 'bg-white text-black';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-white text-black';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-sky-500" />;
      case 'critical': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default: return <Activity className="h-4 w-4 text-black" />;
    }
  };

  const overallStatus = metrics.some(m => m.status === 'critical') ? 'critical' :
                       metrics.some(m => m.status === 'warning') ? 'warning' : 'healthy';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Real-Time System Metrics
            </CardTitle>
            <Badge className={getStatusColor(overallStatus)}>
              System {overallStatus.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {metrics.map((metric) => (
              <Card key={metric.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <metric.icon className="h-4 w-4 text-black" />
                      <span className="font-medium text-sm">{metric.name}</span>
                    </div>
                    {getStatusIcon(metric.status)}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold">{metric.value}</span>
                      <span className="text-sm text-black">{metric.unit}</span>
                    </div>
                    
                    <Progress 
                      value={Math.min((metric.value / metric.threshold) * 100, 100)}
                      className="h-2"
                    />
                    
                    <div className="text-xs text-black">
                      Threshold: {metric.threshold}{metric.unit}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {metrics.slice(0, 2).map((metric) => (
          <Card key={`chart-${metric.id}`}>
            <CardHeader>
              <CardTitle className="text-lg">{metric.name} Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metric.history}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(value) => new Date(value).toLocaleTimeString()}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleString()}
                      formatter={(value) => [`${value}${metric.unit}`, metric.name]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RealTimeMetrics;
